import React, { useEffect, useState, useCallback, memo, useMemo } from 'react';
import { 
  Form, Input, Select, Button, Row, Col, notification, Card, Typography, 
  Space, Avatar, Divider, Alert, Spin, Tooltip, Badge, Progress, Tag, Modal
} from 'antd';
import {
  PhoneOutlined, MailOutlined, UserOutlined, TeamOutlined, 
  ContactsOutlined, SaveOutlined, InfoCircleOutlined,
  MessageOutlined, HeartOutlined, CheckCircleOutlined, 
  EditOutlined, BellOutlined, HomeOutlined, SafetyOutlined, 
  LogoutOutlined, StarOutlined, SafetyCertificateOutlined,
  ClockCircleOutlined, SyncOutlined, MenuOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './ContactForm.css';

// Fonction utilitaire responsive (doit être définie avant toute utilisation)
const isMobile = () => window.innerWidth <= 768;

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Theme configuration
const theme = {
  colors: {
    primary: '#667eea',
    secondary: '#764ba2',
    success: '#52c41a',
    warning: '#fa8c16',
    error: '#ff4d4f',
    info: '#1890ff',
    neutral: '#8c8c8c',
    background: '#f8fafc',
    surface: '#ffffff',
    border: '#e8e8e8'
  },
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.06)',
    md: '0 4px 16px rgba(0, 0, 0, 0.08)',
    lg: '0 8px 32px rgba(0, 0, 0, 0.12)',
    xl: '0 16px 64px rgba(0, 0, 0, 0.16)'
  },
  borderRadius: {
    sm: '6px',
    md: '12px',
    lg: '16px',
    xl: '24px'
  }
};

// Animation configurations
const animations = {
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -30 }
  },
  fadeInScale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  },
  slideInRight: {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 }
  }
};

// Custom hook for user data management
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('Fetching user data from /backend/get_profile.php');
        
        const response = await fetch('/backend/get_profile.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers));

        if (!response.ok) {
          if (response.status === 401) {
            console.log('User not authenticated, redirecting to login');
            navigate('/login');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('API Response:', JSON.stringify(data, null, 2));
        
        // Check if data contains user information (even if success is missing or false)
        if (data.userName || data.nhGuideNumber) {
          const location = [data.street, data.city].filter(Boolean).join(', ') || 'Location not set';
          
          const mappedUserData = {
            name: data.userName || 'User',
            location: location,
            nhNumber: data.nh_guide_number || data.nhGuideNumber || 'Not assigned',
            avatar: data.profile_picture || data.profilePicture || null,
            notifications: data.notifications || 0,
            lastLogin: new Date().toLocaleDateString(),
            role: data.role || 'Member',
          };

          console.log('Mapped user data:', mappedUserData);
          setUserData(mappedUserData);
        } else {
          console.error('API returned no valid user data or success: false', data);
          
          if (data.message && (data.message.toLowerCase().includes('not logged in') || data.message.toLowerCase().includes('authentication'))) {
            console.log('Authentication issue, redirecting to login');
            navigate('/login');
            return;
          }
          
          console.log('Using fallback user data due to API error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
            role: 'Member',
          });
          
          notification.warning({
            message: 'Data Load Warning',
            description: data.message || 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network error - please check your connection');
          notification.error({
            message: 'Network Error',
            description: 'Network error - please check your connection',
            placement: 'topRight',
            duration: 3,
          });
        } else if (err.message.includes('401')) {
          console.log('Authentication error, redirecting to login');
          navigate('/login');
          return;
        } else {
          console.log('Using fallback user data due to error');
          setUserData({
            name: 'Guest User',
            location: 'Location not available',
            nhNumber: 'Not assigned',
            avatar: null,
            notifications: 0,
            lastLogin: new Date().toLocaleDateString(),
            role: 'Member',
          });
          
          notification.warning({
            message: 'Data Load Warning',
            description: 'Some user information could not be loaded',
            placement: 'topRight',
            duration: 3,
            onClose: () => navigate('/login'),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  return { userData, loading, error };
};

// Modern Header Component avec menu hamburger mobile
const ModernHeader = memo(({ userData, loading }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleLogout = useCallback(() => {
    try {
      fetch('/backend/logout.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
        .then(response => response.json().then(data => ({ response, data })))
        .then(({ response, data }) => {
          if (response.ok) {
            notification.success({
              message: 'Logout Successful',
              description: 'You have been successfully logged out.',
              placement: 'topRight',
            });
            navigate('/login');
          } else {
            notification.error({
              message: 'Logout Error',
              description: data.error || 'Error during logout',
              placement: 'topRight',
            });
          }
        })
        .catch(error => {
          notification.error({
            message: 'Connection Error',
            description: 'An error occurred during logout',
            placement: 'topRight',
          });
        });
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'An error occurred during logout',
        placement: 'topRight',
      });
    }
  }, [navigate]);

  return (
    <motion.div 
      className="modern-header"
      style={{ padding: isMobile() ? '0 8px' : '0 32px', minHeight: isMobile() ? 56 : 80, borderRadius: isMobile() ? '0 0 10px 10px' : 0 }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="header-content" style={{ flexDirection: isMobile() ? 'row' : 'row', alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
        <motion.div 
          className="header-left"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Space align="center" size={isMobile() ? 8 : 16}>
            <div className="logo-container" style={{ gap: isMobile() ? 6 : 16 }}>
              <div className="logo-icon">
                 <Avatar 
                      icon={<UserOutlined />}
                      size={isMobile() ? 36 : 64}
                      className="ant-avatar"
                      src="components/nhguidelogo.jpg"
                    />
              </div>
              <div>
                <Title level={3} className="logo-text" style={{ fontSize: isMobile() ? 16 : 24, margin: 0 }}>NH Guide</Title>
                <Text className="logo-subtitle" style={{ fontSize: isMobile() ? 10 : 14 }}>Contact Management</Text>
              </div>
            </div>
          </Space>
        </motion.div>
        {/* Desktop nav */}
        <div className="header-center" style={{ display: isMobile() ? 'none' : 'block' }}>
          <Space size={isMobile() ? 0 : 40}>
            {[{ to: "/home", icon: <HomeOutlined />, label: "Home" },
              { to: "/profile", icon: <UserOutlined />, label: "Profile" },
              { to: "/contact", icon: <ContactsOutlined />, label: "Contact", active: true },
              { to: "/DataPrivacy", icon: <SafetyOutlined />, label: "Privacy" }].map((item, index) => (
              <motion.div
                key={item.to}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Link 
                  to={item.to} 
                  className={`nav-item ${item.active ? 'active' : ''}`}
                  style={{ fontSize: isMobile() ? 12 : 15, padding: isMobile() ? '6px 10px' : '8px 18px', borderRadius: 8 }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              </motion.div>
            ))}
          </Space>
        </div>
        {/* Mobile hamburger */}
        <div style={{ display: isMobile() ? 'flex' : 'none', alignItems: 'center' }}>
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 22 }} />}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: theme.colors.background, borderRadius: 8, marginRight: 4 }}
          />
          <Modal
            open={mobileMenuOpen}
            onCancel={() => setMobileMenuOpen(false)}
            footer={null}
            closable={false}
            width="90vw"
            bodyStyle={{ padding: 0, background: theme.colors.surface, borderRadius: 12 }}
            style={{ top: 8 }}
          >
            <div style={{ padding: 12 }}>
              {[{ to: "/home", icon: <HomeOutlined />, label: "Home" },
                { to: "/profile", icon: <UserOutlined />, label: "Profile" },
                { to: "/contact", icon: <ContactsOutlined />, label: "Contact", active: true },
                { to: "/DataPrivacy", icon: <SafetyOutlined />, label: "Privacy" }].map((item) => (
                <Button
                  key={item.to}
                  type="text"
                  block
                  icon={item.icon}
                  style={{ justifyContent: 'flex-start', marginBottom: 4, fontSize: 15, borderRadius: 8, background: item.active ? theme.colors.primary + '22' : undefined }}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.location.href = item.to;
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Divider style={{ margin: '8px 0' }} />
              <Button
                type="text"
                icon={<LogoutOutlined />}
                block
                style={{ color: theme.colors.error, fontWeight: 600, fontSize: 15 }}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </Modal>
        </div>
        {/* User info & notif */}
        <div className="header-right" style={{ gap: isMobile() ? 6 : 20, display: 'flex', alignItems: 'center' }}>
          <Button type="text" icon={<BellOutlined />} className="notification-btn" style={{ fontSize: isMobile() ? 16 : 20 }}>
            <Badge count={userData?.notifications || 2} size="small" />
          </Button>
          <div className="user-profile" style={{ gap: isMobile() ? 4 : 10 }}>
            <Avatar 
              icon={<UserOutlined />} 
              src={userData?.avatar}
              size={isMobile() ? 28 : 44} 
              className="user-avatar"
              style={{ background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`, border: '2px solid rgba(255,255,255,0.2)' }}
            />
            <div className="user-info">
              <Text strong className="user-name" style={{ fontSize: isMobile() ? 11 : 14 }}>
                {loading ? 'Loading...' : userData?.name || 'User'}
              </Text>
              <Text className="user-role" style={{ fontSize: isMobile() ? 9 : 12 }}>
                {loading ? '...' : userData?.role || 'Member'}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

// Ajout d'une fonction utilitaire pour le style responsive
const mobileStyle = isMobile();

// Enhanced Contact Section Component
const ContactSection = memo(({ 
  title, 
  description, 
  icon, 
  children, 
  color = theme.colors.primary,
  isCompleted = false,
  completionPercentage = 0
}) => (
  <motion.div
    {...animations.fadeInUp}
    transition={{ duration: 0.6, ease: "easeOut" }}
    whileHover={{ y: -2 }}
    style={{ marginBottom: mobileStyle ? 10 : 24 }}
  >
    <Card className="contact-section-card" style={{
      borderRadius: mobileStyle ? 12 : 16,
      boxShadow: mobileStyle ? theme.shadows.sm : theme.shadows.md,
      padding: mobileStyle ? '10px 4px' : '24px',
      marginBottom: mobileStyle ? 8 : 24,
    }}>
      <div className="section-header" style={{ marginBottom: mobileStyle ? 8 : 16 }}>
        <div className="section-title-container" style={{ gap: mobileStyle ? 8 : 20 }}>
          <motion.div 
            className="section-icon" 
            style={{ 
              backgroundColor: `${color}15`, 
              color,
              border: `2px solid ${color}30`,
              fontSize: mobileStyle ? 18 : 28,
              width: mobileStyle ? 32 : 48,
              height: mobileStyle ? 32 : 48,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: mobileStyle ? 8 : 16,
            }}
            transition={{ duration: 0.3 }}
          >
            {icon}
          </motion.div>
          <div className="section-title-content">
            <div className="section-title-row" style={{ alignItems: 'center', gap: 8 }}>
              <Title level={mobileStyle ? 5 : 4} className="section-title" style={{ fontSize: mobileStyle ? 15 : 20, margin: 0 }}>
                {title}
              </Title>
              {isCompleted && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring" }}
                >
                  <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: mobileStyle ? 10 : 13, borderRadius: 8 }}>
                    Complete
                  </Tag>
                </motion.div>
              )}
            </div>
            <Text className="section-description" style={{ fontSize: mobileStyle ? 12 : 15 }}>{description}</Text>
            {completionPercentage > 0 && (
              <div className="section-progress" style={{ marginTop: mobileStyle ? 4 : 10 }}>
                <Progress 
                  percent={completionPercentage} 
                  size={mobileStyle ? 'small' : 'default'} 
                  strokeColor={{
                    '0%': color,
                    '100%': theme.colors.success,
                  }}
                  showInfo={false}
                  style={{ minWidth: mobileStyle ? 60 : 120 }}
                />
                <Text className="progress-text" style={{ fontSize: mobileStyle ? 10 : 13 }}>{completionPercentage}% complete</Text>
              </div>
            )}
          </div>
        </div>
      </div>
      <Divider className="section-divider" style={{ margin: mobileStyle ? '8px 0' : '16px 0' }} />
      <motion.div 
        className="section-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        style={{ padding: 0 }}
      >
        {children}
      </motion.div>
    </Card>
  </motion.div>
));

// Enhanced Form Field Component
const ModernFormField = memo(({ 
  name, 
  label, 
  placeholder, 
  icon, 
  type = 'input', 
  options = [],
  rules = [],
  tooltip,
  disabled = false,
  required = false
}) => {
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  return (
    <motion.div
      className="modern-form-field"
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <Form.Item 
        name={name} 
        label={
          <Space className="field-label-container">
            <span className={`field-label ${required ? 'required' : ''}`}>
              {label}
            </span>
            {tooltip && (
              <Tooltip title={tooltip} placement="top">
                <InfoCircleOutlined className="field-tooltip" />
              </Tooltip>
            )}
          </Space>
        }
        rules={rules}
        className="modern-form-item"
      >
        {type === 'select' ? (
          <Select 
            placeholder={placeholder} 
            size="large"
            className={`modern-select ${focused ? 'focused' : ''} ${hasValue ? 'has-value' : ''}`}
            suffixIcon={
              <motion.div
                animate={{ rotate: focused ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {icon}
              </motion.div>
            }
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(value) => setHasValue(!!value)}
            disabled={disabled}
          >
            {options.map(option => (
              <Option key={option.value} value={option.value}>
                <Space>
                  <span>{option.label}</span>
                </Space>
              </Option>
            ))}
          </Select>
        ) : (
          <Input 
            placeholder={placeholder} 
            size="large"
            prefix={
              <motion.div
                animate={{ 
                  color: focused ? theme.colors.primary : theme.colors.neutral,
                  scale: focused ? 1.1 : 1
                }}
                transition={{ duration: 0.3 }}
              >
                {icon}
              </motion.div>
            }
            className={`modern-input ${focused ? 'focused' : ''} ${hasValue ? 'has-value' : ''}`}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => setHasValue(!!e.target.value)}
            disabled={disabled}
          />
        )}
      </Form.Item>
    </motion.div>
  );
});

// Status Indicator Component
const StatusIndicator = memo(({ status, message }) => {
  const statusConfig = {
    loading: { color: theme.colors.info, icon: <SyncOutlined spin /> },
    success: { color: theme.colors.success, icon: <CheckCircleOutlined /> },
    error: { color: theme.colors.error, icon: <InfoCircleOutlined /> },
    warning: { color: theme.colors.warning, icon: <ClockCircleOutlined /> }
  };

  const config = statusConfig[status] || statusConfig.loading;

  return (
    <motion.div
      className="status-indicator"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      style={{ color: config.color }}
    >
      {config.icon}
      <Text style={{ color: config.color, marginLeft: 8 }}>
        {message}
      </Text>
    </motion.div>
  );
});

const ContactForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [completionStats, setCompletionStats] = useState({
    personal: 0,
    network: 0,
    professional: 0,
    overall: 0
  });
  const { userData, loading: userLoading, error } = useUserData();

  // Animation variants
  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.8,
        staggerChildren: 0.15,
        ease: "easeOut"
      }
    }
  }), []);

  const itemVariants = useMemo(() => ({
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }), []);

  // Calculate completion percentage
  const calculateCompletion = useCallback((data) => {
    const personalFields = ['mainPhone', 'otherContact', 'preferredContactMethod'];
    const networkFields = ['contactPersonName', 'contactDetails', 'contactType'];
    const professionalFields = ['staffName', 'staffContact', 'additionalStaffContact'];

    const calculateSectionCompletion = (fields) => {
      const completed = fields.filter(field => data[field]).length;
      return Math.round((completed / fields.length) * 100);
    };

    const personal = calculateSectionCompletion(personalFields);
    const network = calculateSectionCompletion(networkFields);
    const professional = calculateSectionCompletion(professionalFields);
    const overall = Math.round((personal + network + professional) / 3);

    return { personal, network, professional, overall };
  }, []);

  const fetchContactInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/save_contact_info.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok && !data.error) {
        const formValues = {
          mainPhone: data.mainPhone || '',
          otherContact: data.otherContact || '',
          preferredContactMethod: data.preferredContactMethod || '',
          contactPersonName: data.contactPersonName || '',
          contactDetails: data.contactDetails || '',
          contactType: data.contactType || '',
          staffName: data.staffName || '',
          staffContact: data.staffContact || '',
          additionalStaffContact: data.mainPhone || '',
        };

        form.setFieldsValue(formValues);
        setFormData(formValues);
        setCompletionStats(calculateCompletion(formValues));
      } else {
        notification.error({
          message: 'Error Loading Data',
          description: data.error || 'Failed to load contact information.',
          placement: 'topRight',
          duration: 4,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'Unable to connect to the server. Please try again.',
        placement: 'topRight',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  }, [form, calculateCompletion]);

  useEffect(() => {
    fetchContactInfo();
  }, [fetchContactInfo]);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const response = await fetch('/backend/save_contact_info.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(values),
      });

      const data = await response.json();
      if (response.ok) {
        notification.success({
          message: 'Information Saved Successfully',
          description: 'Your contact information has been updated and secured.',
          placement: 'topRight',
          duration: 4,
          icon: <CheckCircleOutlined style={{ color: theme.colors.success }} />,
        });
        setIsEditing(false);
        setFormData(values);
        setCompletionStats(calculateCompletion(values));
      } else {
        notification.error({
          message: 'Save Failed',
          description: data.error || 'Failed to save contact information.',
          placement: 'topRight',
          duration: 4,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Connection Error',
        description: 'Unable to save information. Please check your connection.',
        placement: 'topRight',
        duration: 4,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    form.setFieldsValue(formData);
  }, [form, formData]);

  if (error) {
    return (
      <div className="contact-form-wrapper">
        <div className="loading-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="loading-content"
          >
            <Text type="danger">{error}</Text>
            <Button 
              type="primary" 
              onClick={() => window.location.reload()} 
              style={{ marginTop: 16, display: 'block', margin: '16px auto' }}
            >
              Retry
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="contact-form-wrapper">
        <div className="loading-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="loading-content"
          >
            <Spin size="large" />
            <Title level={4} style={{ marginTop: 24, color: theme.colors.primary }}>
              Loading Contact Information
            </Title>
            <Text style={{ color: theme.colors.neutral }}>
              Please wait while we retrieve your data...
            </Text>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-form-wrapper">
      <ModernHeader userData={userData} loading={userLoading} />
      
      <div className="contact-form-container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="contact-content"
        >
          <motion.div variants={itemVariants} className="page-header">
            <Card className="page-header-card">
              <div className="page-header-content">
                <div className="page-title-section">
                  
                    <ContactsOutlined />
                  
                  <div className="page-title-content">
                    <Title level={1} className="page-title">
                      Contact Management
                    </Title>
                    <Paragraph className="page-description">
                      Manage your contact information, network connections, and service provider details
                    </Paragraph>
                    <div className="page-stats">
                      <Space size={24}>
                        <div className="stat-item">
                          <Text strong style={{ color: theme.colors.primary }}>
                            {completionStats.overall}%
                          </Text>
                          <Text className="stat-label">Complete</Text>
                        </div>
                        <div className="stat-item">
                          <Text strong style={{ color: theme.colors.success }}>
                            {Object.values(formData).filter(Boolean).length}
                          </Text>
                          <Text className="stat-label">Fields Filled</Text>
                        </div>
                        <div className="stat-item">
                          <SafetyCertificateOutlined style={{ color: theme.colors.success }} />
                          <Text className="stat-label">Secure</Text>
                        </div>
                      </Space>
                    </div>
                  </div>
                </div>
                
                <div className="page-actions">
                  <Space size={16}>
                    {!isEditing && (
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          type="primary"
                          icon={<EditOutlined />}
                          size="large"
                          onClick={() => setIsEditing(true)}
                          className="edit-btn"
                        >
                          Edit Information
                        </Button>
                      </motion.div>
                    )}
                    {saving && <StatusIndicator status="loading" message="Saving changes..." />}
                  </Space>
                </div>
              </div>
            </Card>
          </motion.div>

          <AnimatePresence>
            {isEditing && (
              <motion.div
                {...animations.fadeInScale}
                transition={{ duration: 0.4 }}
              >
                <Alert
                  message="Edit Mode Active"
                  description="You can now modify your contact information. All changes are automatically validated and securely saved."
                  type="info"
                  showIcon
                  className="edit-mode-alert"
                  action={
                    <Button size="small" type="text" onClick={() => setIsEditing(false)}>
                      Exit Edit Mode
                    </Button>
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Form form={form} layout="vertical" onFinish={onFinish} className="modern-contact-form">
            <motion.div variants={itemVariants}>
              <ContactSection
                title="Personal Contact Information"
                description="Your primary contact details and preferred communication methods"
                icon={<PhoneOutlined />}
                color={theme.colors.primary}
                isCompleted={completionStats.personal === 100}
                completionPercentage={completionStats.personal}
              >
                <Row gutter={[16, 24]}>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="mainPhone"
                      label="Primary Phone Number"
                      placeholder="(123) 456-7890"
                      icon={<PhoneOutlined />}
                      tooltip="Your main phone number for important communications"
                      disabled={!isEditing}
                      required
                      rules={[
                        { required: true, message: 'Please enter your phone number' },
                        { pattern: /^[\d\s\-\(\)]+$/, message: 'Please enter a valid phone number' }
                      ]}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="otherContact"
                      label="Alternative Contact"
                      placeholder="Email or Social Media"
                      icon={<MailOutlined />}
                      tooltip="Alternative way to reach you (email, social media, etc.)"
                      disabled={!isEditing}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="preferredContactMethod"
                      label="Preferred Contact Method"
                      placeholder="Select preferred method"
                      type="select"
                      icon={<MessageOutlined />}
                      tooltip="How would you prefer to be contacted?"
                      disabled={!isEditing}
                      required
                      options={[
                        { value: 'phone', label: '📞 Phone Call' },
                        { value: 'email', label: '📧 Email' },
                        { value: 'text', label: '💬 Text Message' },
                        { value: 'social', label: '🌐 Social Media' }
                      ]}
                      rules={[{ required: true, message: 'Please select your preferred contact method' }]}
                    />
                  </Col>
                </Row>
              </ContactSection>
            </motion.div>

            <motion.div variants={itemVariants}>
              <ContactSection
                title="Personal Network"
                description="Important people in your support network and personal connections"
                icon={<HeartOutlined />}
                color={theme.colors.success}
                isCompleted={completionStats.network === 100}
                completionPercentage={completionStats.network}
              >
                <Row gutter={[16, 24]}>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="contactPersonName"
                      label="Emergency Contact Name"
                      placeholder="Full Name"
                      icon={<UserOutlined />}
                      tooltip="Someone who can be contacted in case of emergency"
                      disabled={!isEditing}
                      required
                      rules={[{ required: true, message: 'Please enter an emergency contact name' }]}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="contactDetails"
                      label="Contact Information"
                      placeholder="Phone number or email"
                      icon={<ContactsOutlined />}
                      tooltip="How to reach your emergency contact"
                      disabled={!isEditing}
                      required
                      rules={[{ required: true, message: 'Please enter contact details' }]}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="contactType"
                      label="Relationship"
                      placeholder="Select relationship"
                      type="select"
                      icon={<HeartOutlined />}
                      tooltip="Your relationship with this contact person"
                      disabled={!isEditing}
                      required
                      options={[
                        { value: 'family', label: '👨‍👩‍👧‍👦 Family Member' },
                        { value: 'friend', label: '👥 Friend' },
                        { value: 'colleague', label: '💼 Colleague' },
                        { value: 'neighbor', label: '🏠 Neighbor' },
                        { value: 'other', label: '🤝 Other' }
                      ]}
                      rules={[{ required: true, message: 'Please select the relationship type' }]}
                    />
                  </Col>
                </Row>
              </ContactSection>
            </motion.div>

            <motion.div variants={itemVariants}>
              <ContactSection
                title="Professional Support Network"
                description="Healthcare providers, social workers, and other professional support contacts"
                icon={<TeamOutlined />}
                color={theme.colors.warning}
                isCompleted={completionStats.professional === 100}
                completionPercentage={completionStats.professional}
              >
                <Row gutter={[16, 24]}>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="staffName"
                      label="Primary Staff Contact"
                      placeholder="Staff Member Name"
                      icon={<UserOutlined />}
                      tooltip="Your main contact person at service organizations"
                      disabled={!isEditing}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="staffContact"
                      label="Staff Contact Information"
                      placeholder="Phone or Email"
                      icon={<PhoneOutlined />}
                      tooltip="How to reach your primary staff contact"
                      disabled={!isEditing}
                    />
                  </Col>
                  <Col xs={24} lg={8}>
                    <ModernFormField
                      name="additionalStaffContact"
                      label="Secondary Staff Contact"
                      placeholder="Backup contact information"
                      icon={<TeamOutlined />}
                      tooltip="Alternative staff member or backup contact"
                      disabled={!isEditing}
                    />
                  </Col>
                </Row>
              </ContactSection>
            </motion.div>

            <AnimatePresence>
              {isEditing && (
                <motion.div
                  {...animations.fadeInScale}
                  transition={{ duration: 0.5 }}
                  className="form-actions"
                >
                  <Card className="actions-card">
                    <div className="actions-content">
                      <div className="actions-info">
                        <InfoCircleOutlined className="actions-icon" />
                        <div>
                          <Text strong>Ready to save your changes?</Text>
                          <br />
                          <Text type="secondary">
                            All information is encrypted and stored securely.
                          </Text>
                        </div>
                      </div>
                      <Space size={16}>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button 
                            size="large"
                            onClick={handleCancel}
                            className="cancel-btn"
                          >
                            Cancel Changes
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={saving}
                            icon={<SaveOutlined />}
                            className="save-btn"
                          >
                            {saving ? 'Saving Changes...' : 'Save Changes'}
                          </Button>
                        </motion.div>
                      </Space>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </Form>

          <motion.div variants={itemVariants}>
            <Card className="help-section">
              <div className="help-content">
                <div className="help-header">
                  <div className="help-icon">
                    <StarOutlined />
                  </div>
                  <div>
                    <Title level={4} className="help-title">
                      Tips for Better Contact Management
                    </Title>
                    <Text className="help-subtitle">
                      Keep your information current for the best experience
                    </Text>
                  </div>
                </div>
                <Row gutter={[24, 16]} className="help-tips">
                  <Col xs={24} md={8}>
                    <div className="tip-item">
                      <CheckCircleOutlined className="tip-icon" />
                      <div>
                        <Text strong>Stay Updated</Text>
                        <br />
                        <Text type="secondary">
                          Update your contact information whenever it changes
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="tip-item">
                      <SafetyCertificateOutlined className="tip-icon" />
                      <div>
                        <Text strong>Privacy First</Text>
                        <br />
                        <Text type="secondary">
                          All information is encrypted and kept confidential
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={8}>
                    <div className="tip-item">
                      <HeartOutlined className="tip-icon" />
                      <div>
                        <Text strong>Emergency Ready</Text>
                        <br />
                        <Text type="secondary">
                          Ensure emergency contacts are always reachable
                        </Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactForm;
